import { useCallback, useRef, useState } from 'react'
import { AnnotationDataBoundingBox } from '../types/models/annotation'
import { Project } from '../types/models/project'
import { Tag } from '../types/models/tag'
import { uuidV4, ExportStatus, resolveTagName } from '../utils/exportSnippets'
import { renderSnippetToPNG } from '../utils/snippetRenderer'
import {
  useCreateProjectMutation,
  useUploadInitMutation,
  useUploadProjectContentMutation,
} from '../services/projectsApi'
import { annotationsApi } from '../services/annotationApi'
import { contentApi } from '../services/contentApi'
import { datasetApi } from '../services/datasetApi'
import { tagApi } from '../services/tagApi'
import { apiTags } from '../services/apiTags'
import { useAppDispatch } from '../store/hooks'
import {
  publishExportProgress,
  isExportCancelRequested,
  resetExportCancel,
} from '../utils/exportProgressBus'

export interface ExportItem {
  contentId: string
  boxes: AnnotationDataBoundingBox[]
}

export interface StartExportParams {
  mode: 'existing' | 'new'
  targetProjectId?: string
  newProjectName?: string
  datasetSplit: 'training' | 'validation'
  uploadAsUnannotated: boolean
  imageSize: number
  items: ExportItem[]
  resolveImageObjectUrl: (contentId: string) => Promise<string> | string
  classificationProjects: Project[]
  tags?: Tag[]
  sourceProjectId: string
  // When true, rotate snippet so the long side of the OBB is vertical
  rotateLongSideVertical?: boolean
}

interface UseExportSnippetsState {
  status: ExportStatus
  error: string
  resultCount: number
  finalTargetProjectId: string
  finalTargetProjectName: string
}

export function useExportSnippets() {
  const dispatch = useAppDispatch()
  const [createProject] = useCreateProjectMutation()
  const [uploadInit] = useUploadInitMutation()
  const [uploadContent] = useUploadProjectContentMutation()

  const [state, setState] = useState<UseExportSnippetsState>({
    status: 'idle',
    error: '',
    resultCount: 0,
    finalTargetProjectId: '',
    finalTargetProjectName: '',
  })

  const cancelledRef = useRef(false)

  const cancel = useCallback(() => {
    cancelledRef.current = true
  }, [])

  const reset = useCallback(() => {
    cancelledRef.current = false
    setState({
      status: 'idle',
      error: '',
      resultCount: 0,
      finalTargetProjectId: '',
      finalTargetProjectName: '',
    })
  }, [])

  const shouldStop = () => cancelledRef.current || isExportCancelRequested()

  const startExport = useCallback(
    async (params: StartExportParams) => {
      try {
        resetExportCancel()
        cancelledRef.current = false
        setState((s) => ({ ...s, status: 'preparing', error: '', resultCount: 0 }))

        const {
          mode,
          targetProjectId,
          newProjectName,
          datasetSplit,
          imageSize,
          items,
          resolveImageObjectUrl,
          classificationProjects,
          tags,
          sourceProjectId,
        } = params

        if (!items || items.length === 0) {
          throw new Error('No snippets selected')
        }

        console.time('[useExportSnippets] fullExport')
        // Determine target project and dataset
        let targetId = targetProjectId || ''
        let targetDatasetId = ''
        let finalName = ''

        if (mode === 'new') {
          if (!newProjectName || !newProjectName.trim()) {
            throw new Error('Please enter a project name')
          }
          setState((s) => ({ ...s, status: 'creating' }))
          publishExportProgress({
            visible: true,
            phase: 'creating',
            overallDone: 0,
            overallTotal: 0,
            message: 'Creating project',
          })
          const created = await createProject({
            name: newProjectName.trim(),
            annotation_type: 'classification',
          }).unwrap()
          targetId = created.id
          targetDatasetId = created.datasetid
          finalName = created.name || newProjectName.trim()
        } else {
          if (!targetId) throw new Error('Please choose a target project')
          const found = classificationProjects.find((p) => p.id === targetId)
          targetDatasetId = found?.datasetid || ''
          if (!targetDatasetId) {
            throw new Error('Target dataset not found')
          }
          finalName = found?.name || ''
        }

        if (shouldStop()) return

        console.time('[useExportSnippets] grouping')
        // Image-first grouping: contentId -> entries { box, tagName }
        type Entry = { box: AnnotationDataBoundingBox; tagName: string }
        const byContent: Record<string, Entry[]> = {}
        const perTagCount: Record<string, number> = {}
        for (const item of items) {
          for (const box of item.boxes) {
            const tagName = resolveTagName(box, tags)
            if (!byContent[item.contentId]) byContent[item.contentId] = []
            byContent[item.contentId].push({ box, tagName })
            perTagCount[tagName] = (perTagCount[tagName] || 0) + 1
          }
        }
        const contentIds = Object.keys(byContent)
        const overallTotal = contentIds.reduce((acc, cid) => acc + byContent[cid].length, 0)
        console.timeEnd('[useExportSnippets] grouping')

        // Build a color map from provided tags (numbers -> #RRGGBB using project convention)
        const tagColorMap = new Map<string, string>()
        if (tags && tags.length > 0) {
          tags.forEach((t) => {
            const hex = `#${t.color.toString(16).padStart(6, '0').slice(0, 6)}`
            tagColorMap.set(t.name, hex)
            tagColorMap.set(t.id, hex)
          })
        }

        // Initialize perTag totals/done map
        let perTag: Record<string, { done: number; total: number; color?: string }> = {}
        for (const [tagName, count] of Object.entries(perTagCount)) {
          perTag[tagName] = { done: 0, total: count, color: tagColorMap.get(tagName) }
        }
        publishExportProgress({
          visible: true,
          phase: 'preparing',
          overallTotal,
          overallDone: 0,
          perTag,
          imageTotal: contentIds.length,
          imageDone: 0,
          message: 'Preparing images and labels',
        })

        // In-flight image request cache to dedupe concurrent fetches
        const imageRequestCache = new Map<string, Promise<string>>()
        const getImageSrc = async (cid: string): Promise<string> => {
          if (imageRequestCache.has(cid)) return imageRequestCache.get(cid) as Promise<string>
          const attempt = async (retries: number, delayMs: number): Promise<string> => {
            try {
              const url = await Promise.resolve(resolveImageObjectUrl(cid))
              if (!url) throw new Error('empty-url')
              return url
            } catch (e) {
              if (retries <= 0) throw e
              await new Promise((r) => setTimeout(r, delayMs))
              return attempt(retries - 1, Math.min(delayMs * 2, 2000))
            }
          }
          const p = attempt(3, 200)
          imageRequestCache.set(cid, p)
          return p
        }

        let totalUploaded = 0
        const UPLOAD_CHUNK_SIZE = 200
        const IMAGE_FLUSH_WINDOW = 5

        // Per-tag buffers gathered from images
        type LabelEntry = {
          tags: string[]
          external_id: string
          metadata: { bounding_boxes: AnnotationDataBoundingBox[] }
        }
        const perTagBuffers: Record<string, { files: File[]; labels: LabelEntry[] }> = {}

        // Helper: flush all tag buffers (each tag gets its own init/upload)
        const flushAllTags = async () => {
          const tagNames = Object.keys(perTagBuffers)
          for (const tagName of tagNames) {
            const buf = perTagBuffers[tagName]
            if (!buf || buf.files.length === 0) continue
            const labelsBlob = new Blob([JSON.stringify(buf.labels)], { type: 'application/json' })
            const labelsFile = new File([labelsBlob], 'labels.json', { type: 'application/json' })
            const initResp = await uploadInit({
              projectId: targetId,
              name: tagName,
              labels_file: 'labels.json',
              labels_format: 'ag2',
              labels: labelsFile,
            }).unwrap()
            let uploadIdx = 0
            while (uploadIdx < buf.files.length) {
              const chunk = buf.files.slice(uploadIdx, uploadIdx + UPLOAD_CHUNK_SIZE)
              await uploadContent({
                upload_id: initResp.upload_id,
                datasetid: targetDatasetId,
                files: chunk,
                labels_format: 'ag2',
                metadata: { datasource: `${tagName}${datasetSplit === 'training' ? ':t' : ':v'}` },
              }).unwrap()
              uploadIdx += UPLOAD_CHUNK_SIZE
            }
            totalUploaded += buf.files.length
            const prev = perTag[tagName] || { done: 0, total: 0 }
            const nextPerTag = {
              ...perTag,
              [tagName]: {
                ...prev,
                done: Math.min((prev.done || 0) + buf.files.length, prev.total || buf.files.length),
              },
            }
            perTag = nextPerTag
            publishExportProgress({ overallDone: totalUploaded, perTag: nextPerTag })
            // clear buffer
            perTagBuffers[tagName] = { files: [], labels: [] }
          }
        }

        // Process images and gather per-tag buffers; flush every 5 images
        let imagesProcessed = 0
        for (let i = 0; i < contentIds.length; i++) {
          if (shouldStop()) break
          const cid = contentIds[i]
          const imageSrc = await getImageSrc(cid)
          const entries = byContent[cid]
          for (const entry of entries) {
            const external_id = `${uuidV4()}.png`
            const blob = await renderSnippetToPNG({
              imageSrc,
              box: entry.box,
              canvasSize: imageSize,
              rotateLongSideVertical: params.rotateLongSideVertical === true,
            })
            const file = new File([blob], external_id, { type: 'image/png' })
            if (!perTagBuffers[entry.tagName])
              perTagBuffers[entry.tagName] = { files: [], labels: [] }
            perTagBuffers[entry.tagName].files.push(file)
            perTagBuffers[entry.tagName].labels.push({
              tags: [entry.tagName],
              external_id,
              metadata: { bounding_boxes: [] as AnnotationDataBoundingBox[] },
            })
          }
          imagesProcessed += 1
          publishExportProgress({ imageDone: imagesProcessed })
          const isLastImage = i === contentIds.length - 1
          if (
            imagesProcessed % IMAGE_FLUSH_WINDOW === 0 ||
            (isLastImage && imagesProcessed % IMAGE_FLUSH_WINDOW !== 0)
          ) {
            await flushAllTags()
          }
        }

        // Invalidate caches
        try {
          dispatch(
            annotationsApi.util.invalidateTags([
              { type: apiTags.AnnotationStats, id: targetDatasetId },
              { type: apiTags.AnnotationStats, id: 'LIST' },
              { type: apiTags.Annotation, id: 'LIST' },
            ])
          )
          dispatch(
            contentApi.util.invalidateTags([
              { type: apiTags.Content, id: 'LIST' },
              { type: apiTags.Metadata, id: sourceProjectId },
              { type: apiTags.Metadata, id: 'LIST' },
            ])
          )
          dispatch(
            datasetApi.util.invalidateTags([
              { type: apiTags.Dataset, id: targetDatasetId },
              { type: apiTags.DatasetList, id: 'LIST' },
            ])
          )
          dispatch(
            tagApi.util.invalidateTags([
              { type: apiTags.Tag, id: 'LIST' },
              { type: apiTags.TagProperties, id: targetDatasetId },
            ])
          )
        } catch {}

        setState({
          status: 'success',
          error: '',
          resultCount: totalUploaded,
          finalTargetProjectId: targetId,
          finalTargetProjectName: finalName,
        })
        publishExportProgress({
          phase: 'success',
          message: 'Export complete',
          overallDone: totalUploaded,
          imageDone: contentIds.length,
        })
        console.timeEnd('[useExportSnippets] fullExport')
      } catch (error: unknown) {
        console.error('[useExportSnippets] error', error)
        const message =
          typeof error === 'object' && error && 'message' in error
            ? String((error as { message?: unknown }).message || 'Export failed')
            : 'Export failed'
        setState((s) => ({
          ...s,
          status: 'error',
          error: message,
        }))
        publishExportProgress({
          phase: 'error',
          error:
            typeof error === 'string'
              ? error
              : ((error as { message?: unknown }).message as string) || 'Export failed',
        })
      }
    },
    [createProject, uploadInit, uploadContent, dispatch]
  )

  return {
    ...state,
    startExport,
    cancel,
    reset,
  }
}
